import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccount, useConnect, useSignMessage, useDisconnect } from 'wagmi'
import { injected } from 'wagmi/connectors'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { toast } from 'sonner'
import DarkModeToggle from '../components/DarkModeToggle'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function ConnectWallet() {
  const navigate = useNavigate()
  const { setAuth } = useAuth()
  const { isDarkMode } = useTheme()
  const { connectors, connectAsync } = useConnect()
  const { disconnectAsync } = useDisconnect()
  const { isConnected } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const [isLoadingDesktop, setIsLoadingDesktop] = useState(false)
  const [isLoadingMobile, setIsLoadingMobile] = useState(false)
  const [showMobileAlert, setShowMobileAlert] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Show alert after 1 second of loading
  React.useEffect(() => {
    let timer
    if (isLoadingMobile) {
      timer = setTimeout(() => {
        setShowMobileAlert(true)
      }, 1000)
    } else {
      setShowMobileAlert(false)
    }
    return () => clearTimeout(timer)
  }, [isLoadingMobile])

  // ORIGINAL DESKTOP VERSION - EXACTLY AS IT WAS
  const handleAuthDesktop = async () => {
    try {
      setIsLoadingDesktop(true)

      if (isConnected) {
        await disconnectAsync()
      }

      const { accounts } = await connectAsync({ connector: injected() })
      const account = accounts[0]

      if (!account) {
        throw new Error('Failed to retrieve wallet account')
      }

      // Save provider for later use in Home.js
      if (window.ethereum) {
        window.__PR_APP_PROVIDER = window.ethereum
        console.log('✓ Desktop provider saved to window.__PR_APP_PROVIDER')
      }

      // Request message from backend
      const requestResponse = await axios.post(`${API}/auth/request-message`, {
        address: account,
      })

      const { message } = requestResponse.data

      // Sign message
      const signature = await signMessageAsync({ message })

      // Verify signature
      const verifyResponse = await axios.post(`${API}/auth/verify`, {
        address: account,
        message,
        signature,
      })

      const { token } = verifyResponse.data
      setAuth(token, account)

      // Check if profile exists
      try {
        await axios.get(`${API}/profiles/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        navigate('/home')
      } catch (err) {
        if (err.response?.status === 404) {
          navigate('/create-profile')
        }
      }
    } catch (err) {
      toast.error(err.message || 'Authentication failed')
      console.error('Authentication error:', err)
    } finally {
      setIsLoadingDesktop(false)
    }
  }

  // MODIFIED MOBILE VERSION
  const handleAuthMobile = async () => {
    try {
      setIsLoadingMobile(true)

      if (isConnected) {
        await disconnectAsync()
      }

      // Mobile: Check for in-app browser first, then WalletConnect
      const hasInjected = typeof window !== 'undefined' && window.ethereum
      let selectedConnector
      
      if (hasInjected) {
        // We're in a wallet's in-app browser
        selectedConnector = connectors.find(c => c.id === 'injected')
      } else {
        // Use WalletConnect for standalone mobile browsers
        selectedConnector = connectors.find(c => c.id === 'walletConnect')
      }
      
      if (!selectedConnector) {
        throw new Error('No wallet connector available. Please install MetaMask or use a Web3 browser.')
      }

      const result = await connectAsync({ connector: selectedConnector })
      const account = result.accounts?.[0]

      if (!account) {
        throw new Error('Failed to retrieve wallet account')
      }

      // Save provider for later use in Home.js (WalletConnect or injected)
      try {
        if (selectedConnector.id === 'walletConnect' && result.connector) {
          // WalletConnect provider
          const wcProvider = await result.connector.getProvider()
          window.__PR_APP_PROVIDER = wcProvider
          console.log('✓ WalletConnect provider saved to window.__PR_APP_PROVIDER')

          // START OF REVISED LOGIC: User Instruction for Signature
          // Display a clear prompt to guide the user to the wallet app for the pending signature.
          toast.info('🚀 Please switch back to your wallet app NOW to sign the authentication message.', {
            duration: 8000,
            id: 'signature-prompt'
          })
          // A brief pause before the signMessageAsync call, allowing the user to initiate the switch.
          await new Promise(resolve => setTimeout(resolve, 500))
          // END OF REVISED LOGIC

        } else if (window.ethereum) {
          // Injected provider (in-app browser)
          window.__PR_APP_PROVIDER = window.ethereum
          console.log('✓ Injected provider saved to window.__PR_APP_PROVIDER')
        }
      } catch (providerError) {
        console.error('Provider save error:', providerError)
        // Continue anyway - provider might still work
      }

      // Request message from backend
      const requestResponse = await axios.post(`${API}/auth/request-message`, {
        address: account,
      })

      const { message } = requestResponse.data

      // Sign message
      // The code pauses here, waiting for the user to switch apps and sign.
      const signature = await signMessageAsync({ message })

      // Verify signature
      const verifyResponse = await axios.post(`${API}/auth/verify`, {
        address: account,
        message,
        signature,
      })

      const { token } = verifyResponse.data
      setAuth(token, account)

      // Check if profile exists
      try {
        await axios.get(`${API}/profiles/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        navigate('/home')
      } catch (err) {
        if (err.response?.status === 404) {
          navigate('/create-profile')
        }
      }
    } catch (err) {
      toast.error(err.message || 'Authentication failed')
      console.error('Authentication error:', err)
    } finally {
      setIsLoadingMobile(false)
    }
  }

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
      <div className="max-w-md w-full mx-4">
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/90 border-purple-100'} backdrop-blur-sm rounded-3xl shadow-2xl p-10 border`}>
          <div className="absolute top-4 right-4">
            <DarkModeToggle />
          </div>
          <div className="text-center mb-8">
            <img 
              src="/pr-logo.png" 
              alt="Public Respect Logo" 
              className="w-32 h-32 mx-auto mb-4"
            />
            <h1 className={`text-5xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-indigo-400 via-purple-400 to-pink-400' : 'from-indigo-600 via-purple-600 to-pink-600'} bg-clip-text text-transparent mb-3`} style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Public Respect
            </h1>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-lg`} style={{fontFamily: 'Inter, sans-serif'}}>
              Connect your wallet to get started
            </p>
          </div>

          <div className="space-y-4">
            {/* Desktop Button - only show on desktop */}
            {!isMobile && (
              <Button
                data-testid="connect-wallet-desktop-button"
                onClick={handleAuthDesktop}
                disabled={isLoadingDesktop}
                className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{fontFamily: 'Inter, sans-serif'}}
              >
                {isLoadingDesktop ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Connecting...</span>
                  </div>
                ) : (
                  'Connect Wallet - Desktop'
                )}
              </Button>
            )}

            {/* Mobile Button - only show on mobile */}
            {isMobile && (
              <Button
                data-testid="connect-wallet-mobile-button"
                onClick={handleAuthMobile}
                disabled={isLoadingMobile}
                className="w-full h-14 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                style={{fontFamily: 'Inter, sans-serif'}}
              >
                {isLoadingMobile ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Connecting...</span>
                  </div>
                ) : (
                  'Connect Wallet - Mobile'
                )}
              </Button>
            )}

            {showMobileAlert && (
              <div className={`mt-4 p-4 rounded-lg border-2 ${isDarkMode ? 'bg-blue-900/50 border-blue-500' : 'bg-blue-50 border-blue-400'} animate-pulse`}>
                <p className={`text-center font-bold ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  🔔 Please go back to your connecting wallet to authenticate
                </p>
              </div>
            )}

            <div className="text-center text-sm text-gray-500">
              <p className="font-medium">All major wallets supported</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Give and receive respect points</p>
          <p className="mt-1">Build your reputation on-chain</p>
        </div>
      </div>
    </div>
  )
}